<template>
    <div class="widget contact-properties">
        <div class="widget-content" :style="gridStyle">
            <div
                v-for="(element, index) in visibleProperties"
                :key="element._id"
                class="drag-row"
                :style="{ gridColumn: 'span 1' }"
            >
                <ContactDetailItem
                    :contact="contact"
                    :label="getLabel(element)"
                    :value="getPropertyValue(element._id)"
                    :inputType="element.type"
                    :options="element.options || []"
                    @edit="updatePropertyValue(element._id, $event)"
                    @add="addPropertyValue(element._id, $event)"
                    @delete="deleteProperty(element._id)"
                    :orderable="false"
                    :draggable="false"
                >
                </ContactDetailItem>
            </div>
            <div v-if="sortedContactProperties.length === 0">{{ $t("message.noPropertiesAdded") }}</div>

            <!-- <div class="info-box" @click="navigateToProfileFields" v-if="showInfo">
                <span class="material-icons info-icon">info</span>
                <span class="info-text">{{ $t("message.profileFieldsDescriptionChat") }}</span>
                <span class="material-icons arrow-icon">arrow_forward</span>
            </div> -->
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ContactDetailItem from "./ContactDetailItem.vue";
import log2 from "../../../utils/log2";
import StringUtils from "../../../utils/StringUtils";

const log = log2("ContactProperties");

export default {
    name: "ContactProperties",
    components: {
        ContactDetailItem,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
        showWidgetTitle: {
            type: Boolean,
            default: true,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        columns: {
            type: Number,
            default: 1,
        },
        orderable: {
            type: Boolean,
            default: false,
        },
        maxVisibleRows: {
            type: Number,
            default: null,
        },
        showMore: {
            type: Boolean,
            default: false,
        },
        showInfo: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            sortedContactProperties: [],
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            allContactProperties: (state) => state.contactProperty.data || [],
        }),
        gridStyle() {
            return {
                display: "grid",
                gridTemplateColumns: `repeat(${this.columns}, 1fr)`,
                gap: "10px",
            };
        },
        visibleProperties() {
            if (!this.maxVisibleRows || this.showMore) {
                return this.sortedContactProperties;
            }

            const maxItems = this.maxVisibleRows * this.columns;
            return this.sortedContactProperties.slice(0, maxItems);
        },
    },
    created() {
        this.initializeSortedProperties();
    },
    watch: {
        showMore(newVal) {},
    },
    methods: {
        ...mapActions({
            createContactProperty: "visitor/createContactProperty",
            createNewContactProperty: "contactProperty/createContactProperty",
            updateContactFields: "contact/updateContactFields",
            deleteContactProperty: "visitor/deleteContactProperty",
        }),
        getKey() {
            return StringUtils.hashCodeObj(this.sortedContactProperties);
        },
        initializeSortedProperties() {
            this.sortedContactProperties = this.allContactProperties.map((p) => ({ ...p, value: this.getPropertyValue(p._id) }));
        },
        getLabel(property) {
            return property.label || property.name;
        },
        getPropertyValue(propertyId) {
            const property = (this.contact.contactProperties || []).find((cp) => this.isPropertyMatch(cp, propertyId));
            return property ? property.value : null;
        },
        isPropertyMatch(cp, propertyId) {
            if (typeof cp.property === "object" && cp.property !== null) {
                return cp.property._id === propertyId;
            } else {
                return cp.property === propertyId;
            }
        },
        async updatePropertyValue(propertyId, newValue) {
            await this.createVisitorAttribute({
                key: propertyId,
                value: newValue,
            });
        },
        async addPropertyValue(propertyId, newValue) {
            await this.createVisitorAttribute({
                key: propertyId,
                value: newValue,
            });
        },
        async createVisitorAttribute(form) {
            try {
                let propertyId = form.key;
                if (form.newKey) {
                    const newContactProperty = await this.createNewContactProperty({
                        name: form.newKey,
                        label: form.newKey,
                        type: "text",
                    });
                    propertyId = newContactProperty._id;
                }

                const res = await this.createContactProperty({
                    visitorId: this.contact._id,
                    payload: {
                        property: propertyId,
                        value: form.value,
                    },
                });

                // todo: undefined property check   
                const updatedProperties = [...(this.contact.contactProperties || [])];
                const existingPropertyIndex = updatedProperties.findIndex((p) => this.isPropertyMatch(p, propertyId));

                if (existingPropertyIndex !== -1) {
                    updatedProperties[existingPropertyIndex] = res;
                } else {
                    updatedProperties.push(res);
                }

                await this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        contactProperties: updatedProperties,
                    },
                });

                this.$message.success(this.$t("message.propertyAddedUpdated"));
            } catch (error) {
                log.error("Error in createVisitorAttribute:", { error });
                this.$message.error(this.$t("message.propertyError"));
            }
        },
        async deleteProperty(propertyId) {
            try {
                await this.deleteContactProperty({
                    visitorId: this.contact._id,
                    contactPropertyId: propertyId,
                });

                const updatedProperties = (this.contact.contactProperties || []).filter(
                    (cp) => !this.isPropertyMatch(cp, propertyId)
                );

                await this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        contactProperties: updatedProperties,
                    },
                });

                this.$message.success(this.$t("message.propertyDeleted"));
            } catch (error) {
                log.error("Error in deleteProperty:", { error });
                this.$message.error(this.$t("message.propertyDeleteError"));
            }
        },
        navigateToProfileFields() {
            this.$router.push(`/visitors?tab=profile-fields`);
        },
    },
};
</script>

<style lang="scss" scoped>
.widget {
    background-color: #fff;

    .widget-header {
        padding: 15px;
        border-bottom: 1px solid #e0e0e0;

        .widget-title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            margin: 0;
        }
    }

    .widget-content {
        padding: 15px;
    }
}

.contact-properties {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;

    .drag-rows {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .drag-row {
        padding: 5px;
        border-radius: 4px;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .ghost {
        opacity: 0.5;
        background: #c8ebfb;
    }

}

.info-box {
    background-color: #f5f5f5;
    padding: 12px 15px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 15px;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;

    &:hover {
        background-color: #e8f4fd;
        border-color: #c8e6ff;
    }

    .info-icon {
        margin-right: 10px;
        color: #1890ff;
    }

    .info-text {
        flex: 1;
        font-size: 14px;
        color: #333;
    }

    .arrow-icon {
        margin-left: 10px;
        color: #1890ff;
    }
}
</style>
